import React, { createContext, useState, useContext, ReactNode, useMemo, useCallback, useEffect } from 'react';
import { saveCurrentState, loadCurrentState } from './storage';

interface NewPreemieContextType {
  anamnesis: string;
  birthReason: string;
  gestationalAgeWeeks: number | null;
  gestationalAgeDays: number | null;
  weightAssessment: number | null;
  gender: 'זכר' | 'נקבה' | null;
  setAnamnesis: (anamnesis: string) => void;
  setBirthReason: (birthReason: string) => void;
  setGestationalAgeWeeks: (age: number | null) => void;
  setGestationalAgeDays: (days: number | null) => void;
  setWeightAssessment: (weight: number | null) => void;
  setGender: (gender: 'זכר' | 'נקבה' | null) => void;
  updateContext: (
    anamnesis: string,
    birthReason: string,
    gestationalAgeWeeks: number | null,
    gestationalAgeDays: number | null,
    weightAssessment: number | null,
    gender: 'זכר' | 'נקבה' | null
  ) => void;
  resetContext: () => void;
}

const NewPreemieContext = createContext<NewPreemieContextType | undefined>(undefined);

export const useNewPreemieContext = () => {
  const context = useContext(NewPreemieContext);
  if (!context) {
    throw new Error('useNewPreemieContext must be used within a NewPreemieProvider');
  }
  return context;
};

interface NewPreemieProviderProps {
  children: ReactNode;
}

export const NewPreemieProvider: React.FC<NewPreemieProviderProps> = ({ children }) => {
  const [state, setState] = useState<{
    anamnesis: string;
    birthReason: string;
    gestationalAgeWeeks: number | null;
    gestationalAgeDays: number | null;
    weightAssessment: number | null;
    gender: 'זכר' | 'נקבה' | null;
  }>(() => {
    // Load initial state from local storage
    const savedState = loadCurrentState();
    return {
      anamnesis: savedState?.anamnesis || '',
      birthReason: savedState?.birthReason || '',
      gestationalAgeWeeks: savedState?.gestationalAgeWeeks || null,
      gestationalAgeDays: savedState?.gestationalAgeDays ?? null,
      weightAssessment: savedState?.weightAssessment || null,
      gender: savedState?.gender || null,
    };
  });

  const setAnamnesis = useCallback((anamnesis: string) => {
    setState((prev) => {
      const newState = { ...prev, anamnesis };
      saveCurrentState({ anamnesis });
      return newState;
    });
  }, []);

  const setBirthReason = useCallback((birthReason: string) => {
    setState((prev) => {
      const newState = { ...prev, birthReason };
      saveCurrentState({ birthReason });
      return newState;
    });
  }, []);

  const setGestationalAgeWeeks = useCallback((gestationalAgeWeeks: number | null) => {
    setState((prev) => {
      const newState = { ...prev, gestationalAgeWeeks };
      saveCurrentState({ gestationalAgeWeeks });
      return newState;
    });
  }, []);

  const setGestationalAgeDays = useCallback((gestationalAgeDays: number | null) => {
    setState((prev) => {
      const newState = { ...prev, gestationalAgeDays };
      saveCurrentState({ gestationalAgeDays });
      return newState;
    });
  }, []);

  const setWeightAssessment = useCallback((weightAssessment: number | null) => {
    setState((prev) => {
      const newState = { ...prev, weightAssessment };
      saveCurrentState({ weightAssessment });
      return newState;
    });
  }, []);

  const setGender = useCallback((gender: 'זכר' | 'נקבה' | null) => {
    setState((prev) => {
      const newState = { ...prev, gender };
      saveCurrentState({ gender });
      return newState;
    });
  }, []);

  const updateContext = useCallback((anamnesis: string, birthReason: string, gestationalAgeWeeks: number | null, gestationalAgeDays: number | null, weightAssessment: number | null, gender: 'זכר' | 'נקבה' | null) => {
    const newState = { anamnesis, birthReason, gestationalAgeWeeks, gestationalAgeDays, weightAssessment, gender };
    setState(newState);
    saveCurrentState(newState);
  }, []);

  const resetContext = useCallback(() => {
    const emptyState = {
      anamnesis: '',
      birthReason: '',
      gestationalAgeWeeks: null,
      gestationalAgeDays: null,
      weightAssessment: null,
      gender: null,
      medications: {
        prenatalSteroids: false,
        neuroprotectiveMagnesium: false,
        maternalAntibiotics: false
      },
      roomEquipment: {
        heatedIncubator: false,
        roomHeating: false,
        heatingBags: false,
        chemicalMattress: false,
        warmSheets: false,
        portableBlower: false,
        babyHat: false
      },
      ventilationEquipment: {
        pulseSaturationSensor: false,
        mask: false,
        oxygenConcentration: false,
        suctionEquipment: false
      },
      intubationEquipment: {
        laryngoscope: false,
        blade: false,
        tubes: false,
        nasalIntubation: false,
        oralIntubation: false,
        lubricant: false,
        fixation: false,
        tubeDepth: false,
        portableVentilator: false,
        portableMonitor: false
      },
      departmentAdmission: {
        monitoring: false,
        bloodPressure: false,
        temperature: false,
        glucoseLevel: false,
        bloodGas: false,
        bloodCount: false,
        bloodCulture: false,
        bloodType: false,
        g6pd: false,
        urineCMV: false
      },
      apgar: {
        pulse: {},
        breathing: {},
        muscleTone: {},
        skinColor: {},
        touchResponse: {}
      },
      apgarTimer: {
        startTime: null,
        isRunning: false,
        notifiedMinutes: []
      }
    };
    setState(emptyState);
    saveCurrentState(emptyState);
  }, []);

  const contextValue = useMemo(() => ({
    ...state,
    setAnamnesis,
    setBirthReason,
    setGestationalAgeWeeks,
    setGestationalAgeDays,
    setWeightAssessment,
    setGender,
    updateContext,
    resetContext,
  }), [state, setAnamnesis, setBirthReason, setGestationalAgeWeeks, setGestationalAgeDays, setWeightAssessment, setGender, updateContext, resetContext]);

  return <NewPreemieContext.Provider value={contextValue}>{children}</NewPreemieContext.Provider>;
};
